import { Layout } from '@/components/Layout'
import { useToast } from '@/hooks/useToast'

import { useEffect, useMemo, useState } from 'react'

import Skeleton from 'react-loading-skeleton'

import { useDispatch, useSelector } from 'react-redux'
import { ReduxProps } from '@/storage'
import { SideMenuProps } from '@/storage/modules/sideMenu/reducer'

import { Button } from '@/components/Button'
import { FormMusic } from '@/components/FormMusic'

import { Users } from '@/components/Users'
import { Campaigns } from '@/components/Campaigns'
import { Artists } from '@/components/Artists'
import { Licenses } from '@/components/Licenses'
import { Moods } from '@/components/Moods'

import { Musics } from '@/components/Musics'

import { useModal } from '@/hooks/useModal'
import {
  TrackListRemoteProps,
  handleTrackListRemote,
} from '@/storage/modules/trackListRemote/reducer'
import {
  ArtistsProps,
  handleSetArtists,
} from '@/storage/modules/artists/reducer'
import {
  MusicalGenresProps,
  handleSetMusicalGenres,
} from '@/storage/modules/musicalGenres/reducer'
import {
  LicensesProps,
  handleSetLicenses,
} from '@/storage/modules/licenses/reducer'
import { MoodsProps, handleSetMoods } from '@/storage/modules/moods/reducer'
import {
  SocialMediaProps,
  handleSetSocialMedia,
} from '@/storage/modules/socialMedia/reducer'
import { AlbumsProps, AlbumsDataProps } from '@/types/albumsProps'
import { handleSetAlbums } from '@/storage/modules/albums/reducer'

import { handleSetUsers, UsersProps } from '@/storage/modules/users/reducer'
import {
  CampaignsStateProps,
  handleSetCampaigns,
} from '@/storage/modules/campaigns/reducer'
import { FormArtist } from '@/components/FormArtist'
import { FormCampaign } from '@/components/FormCampaign'
import { MusicalGenres } from '@/components/MusicalGenres'
import { Graphics } from '@/components/Graphics'
import { FormMusicalGenres } from '@/components/FormMusicalGenres'
import { Scenes } from '@/components/Scenes'
import { FormScene } from '@/components/FormScene'
import { FormLicense } from '@/components/FormLicense'
import { FormMood } from '@/components/FormMood'
import { FormSocialMedia } from '@/components/FormSocialMedia'
import { Albums } from '@/components/Albums'
import { FormAlbum } from '@/components/FormAlbum'
import { Notifications } from '@/components/Notifications'
import { api } from '@/services/api'
import { MusicalGenresDataProps } from '@/types/musicalGenresProps'
import { LicenseResponseProps } from '@/types/licensesProps'
import { MusicResponseProps } from '@/types/musicProps'
import { MoodResponseProps } from '@/types/moodsProps'
import { ArtistsResponseProps } from '@/types/artistsProps'
import { SocialMedia } from '@/components/SocialMedia'
import { SocialMediaResponseProps } from '@/types/socialMediaProps'
import { UserDataProps } from '@/types/userProps'
import { MetricsOverviewProps, StorageMetricsProps } from '@/types/metricsProps'
import { CampaignDataProps } from '@/types/campaignProps'
import { Events } from '@/components/Events'
import {
  type PaginatedResponseProps,
  normalizePaginationMeta,
} from '@/types/paginationProps'

type CatalogBootstrapResponse = {
  musicsResponse: PaginatedResponseProps<MusicResponseProps>
  artistsResponse: ArtistsResponseProps[]
  genresResponse: MusicalGenresDataProps[]
  licensesResponse: LicenseResponseProps[]
  moodsResponse: MoodResponseProps[]
  socialMediaResponse: SocialMediaResponseProps[]
  albumsResponse: AlbumsDataProps[]
}

type MetricsBootstrapResponse = {
  storage: StorageMetricsProps
  overview: MetricsOverviewProps
}

type MaybeWrappedPayload<T> = T | { data: T }

function extractPayload<T>(payload: MaybeWrappedPayload<T>): T {
  if (
    typeof payload === 'object' &&
    payload !== null &&
    'data' in payload &&
    payload.data !== undefined
  ) {
    return payload.data
  }

  return payload as T
}

let catalogBootstrapPromise: Promise<CatalogBootstrapResponse> | null = null
let usersBootstrapPromise: Promise<UserDataProps[]> | null = null
let campaignsBootstrapPromise: Promise<CampaignDataProps[]> | null = null
let metricsBootstrapPromise: Promise<MetricsBootstrapResponse> | null = null

async function bootstrapCatalogData(): Promise<CatalogBootstrapResponse> {
  if (!catalogBootstrapPromise) {
    catalogBootstrapPromise = Promise.all([
      api.get<PaginatedResponseProps<MusicResponseProps>>('/musics'),
      api.get('/artists'),
      api.get('/genres'),
      api.get('/licenses'),
      api.get('/moods'),
      api.get('/social-media'),
      api.get('/albums'),
    ])
      .then(
        ([
          musicsResponse,
          artistsResponse,
          genresResponse,
          licensesResponse,
          moodsResponse,
          socialMediaResponse,
          albumsResponse,
        ]) => ({
          musicsResponse: musicsResponse.data,
          artistsResponse: artistsResponse.data.data as ArtistsResponseProps[],
          genresResponse: genresResponse.data.data as MusicalGenresDataProps[],
          licensesResponse: licensesResponse.data
            .data as LicenseResponseProps[],
          moodsResponse: moodsResponse.data.data as MoodResponseProps[],
          socialMediaResponse: socialMediaResponse.data
            .data as SocialMediaResponseProps[],
          albumsResponse: albumsResponse.data.data as AlbumsDataProps[],
        }),
      )
      .finally(() => {
        catalogBootstrapPromise = null
      })
  }

  return catalogBootstrapPromise
}

async function bootstrapUsersData(): Promise<UserDataProps[]> {
  if (!usersBootstrapPromise) {
    usersBootstrapPromise = api
      .get('/users')
      .then((response) => response.data.data as UserDataProps[])
      .finally(() => {
        usersBootstrapPromise = null
      })
  }

  return usersBootstrapPromise
}

async function bootstrapCampaignsData(): Promise<CampaignDataProps[]> {
  if (!campaignsBootstrapPromise) {
    campaignsBootstrapPromise = api
      .get('/admin/campaigns')
      .then((response) => {
        const payload = response.data

        if (Array.isArray(payload)) {
          return payload as CampaignDataProps[]
        }

        if (Array.isArray(payload?.data)) {
          return payload.data as CampaignDataProps[]
        }

        if (Array.isArray(payload?.data?.data)) {
          return payload.data.data as CampaignDataProps[]
        }

        return []
      })
      .finally(() => {
        campaignsBootstrapPromise = null
      })
  }

  return campaignsBootstrapPromise
}

async function bootstrapMetricsData(): Promise<MetricsBootstrapResponse> {
  if (!metricsBootstrapPromise) {
    metricsBootstrapPromise = Promise.all([
      api
        .get('/metrics/storage')
        .then((res) =>
          extractPayload(res.data as MaybeWrappedPayload<StorageMetricsProps>),
        ),
      api
        .get('/metrics/overview')
        .then((res) =>
          extractPayload(res.data as MaybeWrappedPayload<MetricsOverviewProps>),
        ),
    ])
      .then(([storage, overview]) => ({
        storage,
        overview,
      }))
      .finally(() => {
        metricsBootstrapPromise = null
      })
  }

  return metricsBootstrapPromise
}

export function Home() {
  const { showToast } = useToast()
  const { openModal } = useModal()

  const [storageMetrics, setStorageMetrics] =
    useState<StorageMetricsProps | null>(null)
  const [overviewMetrics, setOverviewMetrics] =
    useState<MetricsOverviewProps | null>(null)
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false)

  const { artists } = useSelector<ReduxProps, ArtistsProps>(
    (state) => state.artists,
  )
  const { trackListRemote, currentPage } = useSelector<
    ReduxProps,
    TrackListRemoteProps
  >((state) => state.trackListRemote)
  const { musicalGenres } = useSelector<ReduxProps, MusicalGenresProps>(
    (state) => state.musicalGenres,
  )
  const { licenses } = useSelector<ReduxProps, LicensesProps>(
    (state) => state.licenses,
  )
  const { moods } = useSelector<ReduxProps, MoodsProps>((state) => state.moods)
  const { socialMedia } = useSelector<ReduxProps, SocialMediaProps>(
    (state) => state.socialMedia,
  )

  const { albums } = useSelector<ReduxProps, AlbumsProps>(
    (state) => state.albums,
  )

  const { users } = useSelector<ReduxProps, UsersProps>((state) => state.users)
  const { campaigns, hasLoaded: hasLoadedCampaigns } = useSelector<
    ReduxProps,
    CampaignsStateProps
  >((state) => state.campaigns)

  const [isLoading, setIsLoading] = useState(false)

  const dispatch = useDispatch()

  const { tag } = useSelector<ReduxProps, SideMenuProps>(
    (state) => state.sideMenu,
  )

  const excludePath = ['users', 'graphics', 'notifications', 'events']

  const shouldLoadCatalog = [
    'musics',
    'artists',
    'genres',
    'licenses',
    'moods',
    'socialMedia',
    'albums',
  ].includes(tag)

  const useWideLayout = ['graphics', 'events'].includes(tag)

  const hasLoadedMusics = trackListRemote.length > 0 || currentPage > 0

  const hasCatalogData =
    hasLoadedMusics &&
    artists.length > 0 &&
    musicalGenres.length > 0 &&
    licenses.length > 0 &&
    moods.length > 0 &&
    albums.length > 0

  const handleFormatTitle = useMemo(() => {
    switch (tag) {
      case 'artists':
        return `Artists`

      case 'genres':
        return `Musical Genres`

      case 'musics':
        return `Musics`

      case 'users':
        return `Users`

      case 'scenes':
        return 'Scenes'

      case 'licenses':
        return 'Licenses'

      case 'moods':
        return 'Moods'

      case 'campaigns':
        return 'Campaigns'

      case 'events':
        return 'Eventos'

      case 'albums':
        return 'Albums'

      case 'socialMedia':
        return 'Social Media'

      case 'graphics':
        return 'Graphics'

      case 'notifications':
        return 'Notifications'

      default:
        return ''
    }
  }, [tag])

  useEffect(() => {
    if (tag !== 'graphics' || overviewMetrics) return

    let isMounted = true

    async function handleFetchMetricsAWS() {
      try {
        setIsLoadingMetrics(true)
        const response = await bootstrapMetricsData()

        if (!isMounted) return

        setOverviewMetrics(response.overview)
        setStorageMetrics(response.storage)
      } catch (error) {
        if (!isMounted) return

        showToast('Error fetching metric', {
          type: 'error',
          theme: 'colored',
        })
      } finally {
        if (isMounted) {
          setIsLoadingMetrics(false)
        }
      }
    }

    handleFetchMetricsAWS()

    return () => {
      isMounted = false
    }
  }, [overviewMetrics, showToast, tag])

  useEffect(() => {
    if (!shouldLoadCatalog || hasCatalogData) return

    let isMounted = true

    async function loadInitialData() {
      try {
        setIsLoading(true)
        const {
          musicsResponse,
          artistsResponse,
          genresResponse,
          licensesResponse,
          moodsResponse,
          socialMediaResponse,
          albumsResponse,
        } = await bootstrapCatalogData()

        if (!isMounted) return

        const musicsMeta = normalizePaginationMeta(
          musicsResponse.meta,
          musicsResponse.data.length,
        )

        dispatch(
          handleTrackListRemote({
            trackListRemote: musicsResponse.data as MusicResponseProps[],
            currentPage: musicsMeta.currentPage,
            lastPage: musicsMeta.lastPage,
            totalItems: musicsMeta.totalItems,
          }),
        )

        dispatch(
          handleSetArtists({
            artists: artistsResponse,
          }),
        )

        dispatch(
          handleSetMusicalGenres({
            musicalGenres: genresResponse,
          }),
        )

        dispatch(
          handleSetLicenses({
            licenses: licensesResponse,
          }),
        )

        dispatch(
          handleSetMoods({
            moods: moodsResponse,
          }),
        )

        dispatch(
          handleSetSocialMedia({
            socialMedia: socialMediaResponse,
          }),
        )

        dispatch(
          handleSetAlbums({
            albums: albumsResponse,
          }),
        )
      } catch (error) {
        if (!isMounted) return

        showToast('Error fetching data', {
          type: 'error',
          theme: 'colored',
        })
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    loadInitialData()

    return () => {
      isMounted = false
    }
  }, [dispatch, hasCatalogData, shouldLoadCatalog, showToast])

  useEffect(() => {
    if (tag !== 'users' || users.length > 0) return

    let isMounted = true

    async function loadUsersData() {
      try {
        setIsLoading(true)
        const usersResponse = await bootstrapUsersData()

        if (!isMounted) return

        dispatch(handleSetUsers({ users: usersResponse }))
      } catch (error) {
        if (!isMounted) return

        showToast('Error fetching data', {
          type: 'error',
          theme: 'colored',
        })
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    loadUsersData()

    return () => {
      isMounted = false
    }
  }, [dispatch, showToast, tag, users.length])

  useEffect(() => {
    if (tag !== 'campaigns' || hasLoadedCampaigns) return

    let isMounted = true

    async function loadCampaignsData() {
      try {
        setIsLoading(true)
        const campaignsResponse = await bootstrapCampaignsData()

        if (!isMounted) return

        dispatch(handleSetCampaigns({ campaigns: campaignsResponse }))
      } catch (error) {
        if (!isMounted) return

        showToast('Error fetching campaigns', {
          type: 'error',
          theme: 'colored',
        })
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    loadCampaignsData()

    return () => {
      isMounted = false
    }
  }, [dispatch, hasLoadedCampaigns, showToast, tag])

  return (
    <Layout>
      <div
        className={`${
          useWideLayout ? 'w-full max-w-[1480px]' : 'max-w-3xl'
        } pb-8`}
      >
        <div className="flex items-start justify-between gap-4 md:flex-col">
          <div>
            <h1 className="font-bold text-gray-700 leading-6">Home</h1>
            <h3 className="font-bold text-purple-600 leading-8 text-xl">
              {handleFormatTitle}
            </h3>
          </div>

          {!excludePath.includes(tag) && (
            <>
              <Button
                title="Add"
                variant="purple"
                className="w-[88px] shrink-0 md:w-full"
                onClick={() => {
                  switch (tag) {
                    case 'musics':
                      openModal({
                        children: <FormMusic />,
                      })
                      break

                    case 'artists':
                      openModal({
                        children: <FormArtist />,
                      })
                      break

                    case 'genres':
                      openModal({
                        small: true,
                        children: <FormMusicalGenres />,
                      })
                      break

                    case 'scenes':
                      openModal({
                        children: <FormScene />,
                      })
                      break

                    case 'licenses':
                      openModal({
                        small: true,
                        children: <FormLicense />,
                      })
                      break

                    case 'moods':
                      openModal({
                        small: true,
                        children: <FormMood />,
                      })
                      break

                    case 'albums':
                      openModal({
                        small: true,
                        children: <FormAlbum />,
                      })
                      break

                    case 'socialMedia':
                      openModal({
                        small: true,
                        children: <FormSocialMedia />,
                      })
                      break

                    case 'campaigns':
                      openModal({
                        small: true,
                        children: <FormCampaign />,
                      })
                      break

                    default:
                      break
                  }
                }}
              />
            </>
          )}
        </div>

        {tag === 'musics' && trackListRemote && <Musics />}

        {tag === 'artists' && artists && <Artists />}

        {tag === 'genres' && musicalGenres && <MusicalGenres />}

        {tag === 'scenes' && <Scenes />}

        {tag === 'users' && users && <Users />}

        {tag === 'campaigns' && campaigns && <Campaigns />}

        {tag === 'events' && <Events />}

        {tag === 'licenses' && licenses && <Licenses />}

        {tag === 'moods' && moods && <Moods />}

        {tag === 'albums' && albums && <Albums />}

        {tag === 'socialMedia' && socialMedia && <SocialMedia />}

        {tag === 'graphics' &&
          !isLoadingMetrics &&
          overviewMetrics &&
          storageMetrics && (
            <Graphics
              storageMetrics={storageMetrics}
              metricsOverview={overviewMetrics}
            />
          )}

        {tag === 'notifications' && <Notifications />}

        {isLoading && (
          <>
            <Skeleton className="h-[80px] mt-8 rounded-2xl" />
            <Skeleton className="h-[80px] mt-8 rounded-2xl" />
          </>
        )}
      </div>
    </Layout>
  )
}
